import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BudgetSnapshot, Scenario } from '@/engine/types';
import { useDataStore } from '@/stores/data-store';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Modal } from '@/design-system';
import { formatTimestamp } from '@/utils/date';

export type CompareTarget =
  | { kind: 'real'; id: 'real' }
  | { kind: 'scenario'; id: string; name: string }
  | { kind: 'snapshot'; id: string };

interface ComparePickerModalProps {
  open: boolean;
  onClose: () => void;
  currentKind: 'real' | 'scenario' | 'snapshot';
  currentId: string;
  onSelect: (target: CompareTarget) => void;
}

function SectionTitle({ label }: { label: string }) {
  return (
    <div style={{
      fontFamily: sans,
      fontSize: 9.5,
      fontWeight: 700,
      color: P.t3,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      padding: '12px 4px 6px',
    }}>
      {label}
    </div>
  );
}

function PickerRow({ label, sublabel, accent, onClick }: {
  label: string;
  sublabel?: string;
  accent: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 12px',
        background: P.s1,
        border: `1px solid ${P.border}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 6,
        cursor: 'pointer',
        textAlign: 'left',
        outline: 'none',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: sans,
          fontSize: 12.5,
          fontWeight: 600,
          color: P.t1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </div>
        {sublabel && (
          <div style={{
            fontFamily: sans,
            fontSize: 10.5,
            color: P.t3,
            marginTop: 2,
          }}>
            {sublabel}
          </div>
        )}
      </div>
    </button>
  );
}

export function ComparePickerModal({
  open, onClose, currentKind, currentId, onSelect,
}: ComparePickerModalProps) {
  const { t } = useTranslation();
  const scenarios = useDataStore(s => s.scenarios) as unknown as Scenario[];
  const budgetSnapshots = useDataStore(s => s.budgetSnapshots) as BudgetSnapshot[];

  const showReal = currentKind !== 'real';

  const visibleScenarios = useMemo(
    () => scenarios.filter(s => !(currentKind === 'scenario' && s.id === currentId)),
    [scenarios, currentKind, currentId],
  );

  const visibleSnapshots = useMemo(
    () => [...budgetSnapshots]
      .filter(s => !(currentKind === 'snapshot' && s.id === currentId))
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
    [budgetSnapshots, currentKind, currentId],
  );

  const handleSelect = (target: CompareTarget) => {
    onSelect(target);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('budget.comparePicker.title')}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {showReal && (
          <>
            <SectionTitle label={t('budget.comparePicker.realSection')} />
            <PickerRow
              label={t('budget.real')}
              accent={P.bReal}
              onClick={() => handleSelect({ kind: 'real', id: 'real' })}
            />
          </>
        )}

        {visibleScenarios.length > 0 && (
          <>
            <SectionTitle label={t('budget.comparePicker.scenariosSection')} />
            {visibleScenarios.map(s => (
              <PickerRow
                key={s.id}
                label={s.name}
                accent={P.bScenario}
                onClick={() => handleSelect({ kind: 'scenario', id: s.id, name: s.name })}
              />
            ))}
          </>
        )}

        {visibleSnapshots.length > 0 && (
          <>
            <SectionTitle label={t('budget.comparePicker.snapshotsSection')} />
            {visibleSnapshots.map(s => (
              <PickerRow
                key={s.id}
                label={s.label}
                sublabel={formatTimestamp(s.timestamp)}
                accent={P.bHist}
                onClick={() => handleSelect({ kind: 'snapshot', id: s.id })}
              />
            ))}
          </>
        )}

        {!showReal && visibleScenarios.length === 0 && visibleSnapshots.length === 0 && (
          <div style={{
            padding: '24px 12px',
            fontFamily: sans,
            fontSize: 12,
            color: P.t3,
            textAlign: 'center',
          }}>
            {t('budget.comparePicker.empty')}
          </div>
        )}
      </div>
    </Modal>
  );
}
